'use strict';

import * as util from '../util/Common.js';
import {EventManager} from '../util/EventManager.js';
import {Point2D} from '../util/Geometry.js';
import * as domUtil from '../util/Dom.js';
import {UiComponent} from '../ui/UiComponent.js';
import {RelativeContainer} from '../ui/RelativeContainer.js';

const privateMap = util.createPrivateMapper();

function regionEquals(r1, r2) {
  if (r1.length != r2.length) {
    return false;
  }
  const l = r1.length;
  for (let i = 0; i < l; i++) {
    if (r1[i] != r2[i]) {
      return false;
    }
  }
  return true;
}
function regionMapEquals(r1, r2){
  if (!r1 && !r2) {
    return true;
  }
  if (!r1 || !r2) {
    return false;
  }
  for (let k in r1) {
    if (!r2[k] || !regionEquals(r2[k], r1[k])) {
      return false;
    }
  }
  for (let k in r2) {
    if (!r1[k]) {
      return false;
    }
  }
  return true;
}


export class ContentViewSession {
  constructor(){
    this.eventManager = new EventManager(this);
  }

  destroy(){
    this.eventManager.dispatchEvent('destroy');
  }
}

export class ContentView extends UiComponent {
  constructor(domEl, options){
    super(domEl);
    const _self = this;
    const pv = privateMap(this);
    pv.options = options ? options : {};
    pv.relativeContainer = new RelativeContainer(domEl);
    pv.control = false;
    pv.renderer = false;
    pv.resource = false;
    pv.annotationListResources = false;
    pv.annotationListRenderer = false;
    pv.renderTimeout = false;
    pv.annotationDisabled = false;
    pv.canvas = document.createElement('canvas');
    pv.context2d = pv.canvas.getContext('2d');
    domUtil.asAbsoluteFit(pv.canvas);
    pv.lastSelectedAnnotations = {};

    pv.watermarkLayer = document.createElement('div')
    pv.watermarkLayer.style.display = 'none'
    domUtil.asAbsoluteFit(pv.watermarkLayer)

    pv.relativeContainer.domEl.appendChild(pv.canvas)
    pv.relativeContainer.domEl.appendChild(pv.watermarkLayer)

    if (pv.options.eventLayer) {
      pv.eventLayer = pv.options.eventLayer
    } else {
      pv.eventLayer = document.createElement('div')
      domUtil.asAbsoluteFit(pv.eventLayer)
      pv.relativeContainer.domEl.appendChild(pv.eventLayer)
    }

    pv.render = () => {
      pv.renderTimeout = false;
      if (!(pv.relativeContainer.size.x > 0 && pv.relativeContainer.size.y > 0)) {
        return;
      }
      domUtil.canvasResize(pv.canvas, pv.relativeContainer.size);
      if (pv.renderer && pv.control && pv.resource) {
        pv.renderer.render(this, pv.resource, pv.control, pv.session);
        if (!pv.annotationDisabled && pv.annotationListRenderer && pv.annotationListResources) {
          util.each(pv.annotationListResources, (res) => {
            pv.annotationListRenderer.render(this, res, pv.control, pv.session);
          })
        }
        if (pv.session.debug) {
          const ctx = pv.context2d
          if (pv.session.debug.shapes) {
            for (let shape of pv.session.debug.shapes) {
              ctx.save()
              ctx.lineWidth = 10

              if (shape.style) {
                for (let sn in shape.style) {
                  ctx[sn] = shape.style[sn]
                }
              }
              switch (shape.type) {
              case 'point':
                {
                  const px = shape.px
                  ctx.beginPath()
                  ctx.moveTo(px.x, px.y - 50)
                  ctx.lineTo(px.x, px.y + 50)
                  ctx.stroke()
    
                  ctx.beginPath()
                  ctx.moveTo(px.x - 50, px.y)
                  ctx.lineTo(px.x + 50, px.y)
                  ctx.stroke()
                }
              }

              ctx.restore()
            }
          }
          pv.session.debug = null
        }
        domUtil.dispatchEvent(pv.eventLayer, 'ui.contentrender', {
          detail: {
            resource: pv.resource,
            control: pv.control,
            session: pv.session
          }
        });
      }
    }
    domUtil.canvasResize(pv.canvas, pv.relativeContainer.size);
  }

  get renderer(){
    return privateMap(this).renderer;
  }

  setView(renderer, control, resource) {
    const pv = privateMap(this);
    pv.renderer = renderer;
    pv.control = control;
    for (let nm in pv.control) {
      if (!pv.control[nm]) {
        console.error('control not found.', nm)
      }
      pv.control[nm].resource = resource;
      pv.control[nm].viewSize = this.size;
    }
    pv.resource = resource;
    pv.session = new ContentViewSession();
    domUtil.dispatchEvent(pv.eventLayer, 'ui.contentchange', {
      detail: pv.resource
    });
  }

  reset(){
    const pv = privateMap(this);
    this.watermark = null
    if (pv.session) {
      pv.session.destroy();
      pv.session = null;
    }
    if (pv.control) {
    }
    pv.control = null;
    for (let nm in pv.control) {
      pv.control[nm].resource = null;
    }
    pv.renderer = null;
    pv.resource = null;
    pv.annotationListResources = null;
    pv.lastSelectedAnnotations = {};
    pv.canvas.width = pv.canvas.width
    domUtil.canvasResize(pv.canvas, pv.relativeContainer.size);
  }

  set annotationListRenderer(annotationListRenderer){
    privateMap(this).annotationListRenderer = annotationListRenderer;
  }
  get annotationListRenderer(){
    return privateMap(this).annotationListRenderer;
  }

  set annotationDisabled(annotationDisabled){
    privateMap(this).annotationDisabled = annotationDisabled;
    this.focusAnnotation()
    // this.requestRender()
  }
  get annotationDisabled(){
    return privateMap(this).annotationDisabled;
  }

  set annotationListResources(annotationListResources){
    const pv = privateMap(this);
    pv.annotationListResources = annotationListResources;
    domUtil.dispatchEvent(pv.eventLayer, 'ui.annotationchange', {
      detail: pv.annotationListResources
    });
  }
  get annotationListResources(){
    return privateMap(this).annotationListResources;
  }

  get resource(){
    return privateMap(this).resource;
  }

  get eventLayer() {
    return privateMap(this).eventLayer;
  }

  get context2d(){
    return privateMap(this).context2d;
  }

  get center(){
    const pv = privateMap(this);
    return pv.relativeContainer.size.div(2);
  }
  get size(){
    const pv = privateMap(this);
    return pv.relativeContainer.size.clone();
  }
  get control(){
    return privateMap(this).control;
  }
  get session(){
    return privateMap(this).session;
  }
  get focusedAnnotation(){
    return privateMap(this).focusedAnnotation;
  }

  set watermark(wm){
    const pv = privateMap(this)
    pv.watermarkLayer.innerHTML = ''
    pv.watermarkLayer.style.display = 'none'
    if (wm && wm.img) {
      pv.watermarkLayer.appendChild(wm.img)
      pv.watermarkLayer.style.display = ''
    }
  }
  
  // removeControl(name){
  //   const pv = privateMap(this);
  //   const control = pv.controlMap[name];
  //   control.unbind(this);
  //   delete pv.controlMap[name];
  // }
  selectAnnotations(mode, targets) {
    const pv = privateMap(this);
    if (regionMapEquals(pv.lastSelectedAnnotations[mode], targets)) {
      // same with last selection
      return;
    }
    pv.lastSelectedAnnotations[mode] = targets;
    util.each(pv.annotationListResources, (annoList) => {
      const annos = targets && targets[annoList.id] ? targets[annoList.id] : [];
      annoList.setRenderRegions(mode, annos);
    });
    domUtil.dispatchEvent(pv.eventLayer, 'ui.annotationselect', {
      detail: {
        mode: mode,
        targets: targets
      }
    });
    this.requestRender();
  }
  focusAnnotation(listId, annoId) {
    const pv = privateMap(this);
    const focusMode = 'focus';
    const last = pv.focusedAnnotation;
    if (last) {
      if (last.listId == listId && last.annotationId == annoId) {
        // same annotation
        return;
      }
    }
    pv.focusedAnnotation = {
      listId: listId,
      annotationId: annoId
    };
    util.each(pv.annotationListResources, (annoList) => {
      if (annoList.id != listId || annoId == null) {
        annoList.setRenderRegions(focusMode, []);
      } else {
        annoList.setRenderRegions(focusMode, [annoId]);
      }
    });
    domUtil.dispatchEvent(pv.eventLayer, 'ui.annotationfocus', {
      detail: pv.focusedAnnotation
    });
    this.requestRender();
  }
  getAnnotation(listId, annoId) {
    const pv = privateMap(this);
    if (!pv.annotationListResources || !listId) {
      return;
    }
    let annoList;
    util.each(pv.annotationListResources, (al) =>{
      if (al.id == listId) {
        annoList = al;
        return false;
      }
    });
    if (!annoList) {
      return;
    }
    let anno;
    util.each(annoList.getDatas(), (a) =>{
      if (a.id == annoId) {
        anno = a;
        return false;
      }
    });
    return anno;
  }

  requestRender(){
    const pv = privateMap(this);
    if (!pv.renderer) {
      // no renderer
      return;
    }
    if (pv.renderTimeout) {
      // already requested
      return;
    }
    pv.renderTimeout = requestAnimationFrame(pv.render);
  }

  applySize(){
    const pv = privateMap(this);
    pv.relativeContainer.applySize();
    if (pv.control) {
      for (let nm in pv.control) {
        pv.control[nm].viewSize = this.size;
      }
    }
    this.requestRender();
  }
}
